/**
 * The mini-game registry.
 *
 * Keyed by id, so the collision path never names a game and the wire never carries a game's
 * rules — a duel carries `gameId` and both ends look the rest up here. "Or other mini games" is
 * therefore a new file in this directory plus one entry in MINI_GAMES, and no change anywhere
 * else.
 */

import { chicken } from './chicken.js';
import type { MiniGame } from './minigame.js';
import { rockPaperScissors } from './rps.js';

export * from './chicken.js';
export * from './minigame.js';
export * from './rps.js';

/**
 * Every mini-game a duel may be decided by. Order is the tie-break for `pickMiniGame`.
 *
 * **CHICKEN IS IN, AND THE ENTRY COSTS ONE DRAW OF EVERY ROOM'S SEEDED STREAM — PAID ONCE, ON
 * PURPOSE.** With a single entry `pickMiniGame` returned without calling `roll()` at all; with
 * two it calls it, so every draw *after* the first duel in a room lands one slot further along
 * the sequence than it did on the build before this one. That is a real change and it is
 * accepted here rather than worked around, because nothing can observe it:
 *
 *   - No player can see it. A room's seed decides where pellets scatter and where somebody
 *     spawns, and both of those were arbitrary to begin with; a different arbitrary is the same
 *     game.
 *   - Nothing is pinned to a golden value. Every determinism assertion in the project is
 *     self-comparing — two rooms from one seed, replayed and diffed against each other — so
 *     none of them holds a number this shift could contradict.
 *   - There are no replays and no saved rooms. A room exists only while it is being simulated,
 *     so there is no recording from a previous build for the new sequence to disagree with.
 *   - No two hosts ever simulate the same room. `world-hash.ts` compares hosts running the *same*
 *     room from the same seed, and both of them are this build.
 *
 * The alternative — keeping the `length === 1` fast path alive by never registering a second game
 * — is the registry not being an extension point. Pay it once.
 *
 * **DEPLOY ORDER MATTERS AND IS NOT ENFORCEABLE FROM HERE.** A client cannot draw a game its own
 * build has never heard of, so this entry must not reach a *server* until a web build carrying
 * the chicken overlay has been deployed. A bundled Android APK has no OTA route at all and will
 * meet an unknown game id until its owner installs a new one; what that player gets is the
 * unknown-id path the client card built — the card, the freeze, a running countdown, no buttons
 * and a line saying the game arrived with an update they do not have — and the room finishes the
 * duel without them. Survivable, not invisible.
 */
export const MINI_GAMES: readonly MiniGame[] = [rockPaperScissors, chicken];

const BY_ID = new Map(MINI_GAMES.map((game) => [game.id, game]));

/** Undefined for an id this build does not have — a client one version ahead, most likely. */
export function getMiniGame(id: string): MiniGame | undefined {
  return BY_ID.get(id);
}

/**
 * Choose the mini-game for one duel.
 *
 * `roll` is the room's seeded generator, and it is only *called* when there is a choice to make.
 * That fast path is now dead in production — the registry holds two games — and it is kept
 * because it is the honest statement of when a draw is owed: a registry back down to one entry
 * would owe none. Registering chicken is what started consuming the draw; see MINI_GAMES above
 * for why that one-time shift of every room's sequence is accepted rather than avoided.
 */
export function pickMiniGame(roll: () => number): MiniGame {
  if (MINI_GAMES.length === 1) return MINI_GAMES[0];
  const index = Math.floor(roll() * MINI_GAMES.length);
  return MINI_GAMES[index < 0 ? 0 : index >= MINI_GAMES.length ? MINI_GAMES.length - 1 : index];
}
